import { Box } from '@mui/material';
import React, { useCallback, useContext } from 'react';
import { Attachment } from 'sr-types/lib/search/v1/graphql';
import { UserContext } from '../../../../auth/UserContext';
import EditablePanel, { EditablePanelProps } from '../../../../components/EditablePanel';
import { FORM_WIDGET_COMPONENTS } from '../../../../form/formConstants';
import { FormContext, FormContextType } from '../../../../form/FormContext';
import FormWidget from '../../../../form/FormWidget';
import { failedToastMessage } from '../../../../utils/commonUtils';
import { APPROVAL_EDITABLE_PANEL_HEADER_SX } from '../../../constants';
import { associateTagsWithAttachments } from '../../../data/utils';
import { ApprovalInputViewModel } from '../../../types';
import { useCreateApprovalContext } from '../../hooks/useCreateApprovalContext';
import CreateApprovalFormEditablePanelHeader from '../CreateApprovalFormEditablePanelHeader';

export type ApprovalAttachmentsFormFieldProps = Pick<EditablePanelProps, 'isCollapsable' | 'defaultCollapsed'>;

export default function ApprovalAttachmentsFormField({
    isCollapsable,
    defaultCollapsed
}: ApprovalAttachmentsFormFieldProps) {
    const { state, setState } = useContext<FormContextType<ApprovalInputViewModel>>(FormContext);

    const { entity, isSavingApprovalInput } = useCreateApprovalContext();

    const { activeOrganizationAccount } = useContext(UserContext);

    const onDeleteAttachments = useCallback(
        (localAttachments: Attachment[], input: Attachment) => {
            const filteredAttachments = localAttachments.filter(
                (localAttachment) => localAttachment.entityReference.id !== input.entityReference.id
            );
            setState((prevState) => ({
                ...prevState,
                attachments: filteredAttachments
            }));
            return filteredAttachments;
        },
        [setState]
    );

    const onSaveAttachments = useCallback(
        async (_entityId: string, attachments: Attachment[]) => {
            try {
                await associateTagsWithAttachments(attachments, activeOrganizationAccount);
            } catch (error) {
                failedToastMessage('attachment.tags.save.error');
            }
        },
        [activeOrganizationAccount]
    );

    return (
        <EditablePanel
            Header={<CreateApprovalFormEditablePanelHeader titleToken={'approval.attachments.header'} />}
            isCollapsable={isCollapsable}
            defaultCollapsed={defaultCollapsed}
            headerSx={APPROVAL_EDITABLE_PANEL_HEADER_SX}
        >
            <Box
                sx={(theme) => ({
                    borderWidth: 1,
                    borderColor: theme.palette.grey[theme.palette.mode === 'dark' ? 700 : 400],
                    borderStyle: 'solid',
                    borderRadius: 1,
                    paddingInline: 2,
                    paddingBlock: 1,
                    width: '100%'
                })}
            >
                <FormWidget
                    component={FORM_WIDGET_COMPONENTS.attachments}
                    name={'attachments'}
                    entityReference={state.entityReference}
                    entity={entity}
                    onSaveAttachments={onSaveAttachments}
                    onDeleteAttachments={onDeleteAttachments}
                    disableDropzone
                    hideDropzone
                    leftAlignContent={false}
                    showTitle={false}
                    disableButtons={isSavingApprovalInput}
                />
            </Box>
        </EditablePanel>
    );
}
